/**
 * useLocationTracking — Starts/stops GPS location broadcasting for the current user.
 *
 * Call this once in TechLayout and OfficeLayout. It reads userId/userName/userRole
 * from localStorage and starts the location service. Stops on unmount.
 * Only activates when the enableMaps feature flag is true.
 */

import { useEffect, useState, useRef } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { startLocationBroadcast } from '@/services/location.service';
import { getTenantIdSync } from '@/hooks/useTenantId';

interface LocationTrackingState {
  isTracking: boolean;
  error: string | null;
}

export function useLocationTracking(): LocationTrackingState {
  const { tenant } = useTenant();
  const [state, setState] = useState<LocationTrackingState>({
    isTracking: false,
    error: null,
  });
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Feature flag check
    if (!tenant?.featureFlags?.enableMaps) return;

    // Read user identity from localStorage
    const userId = localStorage.getItem('user_id') || localStorage.getItem('tech_id');
    const userName =
      localStorage.getItem('user_name') ||
      localStorage.getItem('tech_name') ||
      localStorage.getItem('logged_in_user') ||
      'Unknown';
    const userRole = localStorage.getItem('user_role') || 'Office';
    const tenantId = tenant.id || getTenantIdSync();

    if (!userId) {
      setState({ isTracking: false, error: 'No user ID found' });
      return;
    }

    // Check geolocation support
    if (!navigator.geolocation) {
      setState({ isTracking: false, error: 'Geolocation not supported' });
      return;
    }

    // Start broadcasting
    const cleanup = startLocationBroadcast(userId, userName, userRole, tenantId);
    cleanupRef.current = cleanup;
    setState({ isTracking: true, error: null });

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      setState({ isTracking: false, error: null });
    };
  }, [tenant?.featureFlags?.enableMaps, tenant?.id]);

  return state;
}
